// src/encounter/services/use-lab-order-search.ts
// The Add lab popup on the agreed lab model (PROD-2849, canvas boards 6-9). It reads Scrips.Master's
// A&P lab search (PROD-2839, LabOrderSearchController):
//
//   GET api/Master/LabCatalogue/order-search?search=&take=   -> LabOrderSearchResult
//   GET api/Master/LabCatalogue/order-search/{id}            -> LabOrderItemDetail (with members)
//
// No route here returns a price, a cost or a margin: physicians never see them.
//
// WHEN THE POPUP USES IT: Master gates both routes behind `Features:LabOrderSearchV3` and
// `Features:LabCatalogueV2Reads` (default off). With a flag off a physician gets 404 with the code
// LAB_ORDER_SEARCH_V3_OFF / LAB_CATALOGUE_V2_READS_OFF. `useLabOrderSearchMode` asks once
// (`take=1`) and answers:
//   'v3'      the route answered 200: the popup runs on it;
//   'legacy'  a flag-off 404, a 503 (Master's lab tables not created here), or any other failure:
//             the current popup (LabOrderCodesList + LabOrderValueSet + the v2 panel read) is used,
//             exactly as before;
//   'pending' still asking (the popup shows the current popup meanwhile).
// The answer must look like an order-search result (`items` and `switchesAvailable`); anything else
// (an HTML error page, a proxy's empty body) is 'legacy' too.
// Once the popup is on v3, its own search and detail errors are shown as errors (with Retry). It never
// falls back to the legacy flow half-way through an order, and it never calls LabVendors or
// LabCatalogue/panels (the A&P session's audit, PROD-2849 comment of 2 Oct).
import { useContext } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';

const BASE = '/api/Master/LabCatalogue/order-search';

/** Master's flag-off codes (LabOrderSearchGateAttribute / LabCatalogueReadGateAttribute). */
export const LAB_ORDER_SEARCH_OFF_CODES = ['LAB_ORDER_SEARCH_V3_OFF', 'LAB_CATALOGUE_V2_READS_OFF'] as const;

/** One lab that can perform an item (no price). */
export interface LabOrderLab {
  labVendorId: string;
  name?: string | null;
  shortCode?: string | null;
  vendorCode?: string | null;
  labPriceLineId?: string | null;
  isPreferred?: boolean;
}

export interface LabOrderSpecimen {
  specimenType?: string | null;
  container?: string | null;
  minVolume?: string | null;
  handling?: string | null;
  source?: string | null;
}

export interface LabOrderQuestion {
  id?: string;
  testId?: string;
  code?: string | null;
  text: string;
  /** YesNo | Time | Choice | Text. */
  answerType?: string | null;
  choices?: string[] | null;
  required?: boolean;
  sentToLabAs?: string | null;
  displayOrder?: number;
}

/** "Single test" | "Panel" | "Clinic panel". */
export type LabOrderItemType = 'Single test' | 'Panel' | 'Clinic panel';

export interface LabOrderSearchItem {
  id: string;
  type: LabOrderItemType | string;
  kind?: string | null;
  name: string;
  clinicCode?: string | null;
  /** The item's own order coding (Scrips.Master #325). Older Master builds leave it out. */
  code?: string | null;
  system?: string | null;
  loinc?: string | null;
  cptCode?: string | null;
  version?: number;
  fromStandardList?: boolean;
  matchedBy?: string | null;
  defaultLab?: LabOrderLab | null;
  /** Preferred | OnlyLab | SetByClinic | PatientsChoice. */
  defaultReason?: string | null;
  /** ClinicDoesNotSendTests | NotInClinicList | NoLabPricesIt | NoPreferredLab | NoLabSet | LabDoesNotPriceIt. */
  patientsChoiceReason?: string | null;
  isPatientsChoice?: boolean;
  /** ContractedLab | PatientChoice. */
  fulfilmentMode?: string | null;
  allowedLabs?: LabOrderLab[] | null;
  canChangeLab?: boolean;
  specimen?: LabOrderSpecimen | null;
  specimens?: LabOrderSpecimen[] | null;
  /** None | Fasting | Timed. */
  prepType?: string | null;
  prepNote?: string | null;
  questions?: LabOrderQuestion[] | null;
  /** Clinic panels: Package | PerTest. */
  billingMode?: string | null;
  testCount?: number | null;
  patientsChoiceTestCount?: number | null;
}

export interface LabOrderMember {
  memberId: string;
  testId?: string | null;
  panelId?: string | null;
  type?: string | null;
  name: string;
  clinicCode?: string | null;
  code?: string | null;
  system?: string | null;
  loinc?: string | null;
  cptCode?: string | null;
  tickedByDefault?: boolean;
  fixed?: boolean;
  isReflex?: boolean;
  reflexRule?: string | null;
  effectiveLabVendorId?: string | null;
  effectiveLabName?: string | null;
  effectiveLabShortCode?: string | null;
  vendorCode?: string | null;
  labPriceLineId?: string | null;
  isPatientsChoice?: boolean;
  patientsChoiceReason?: string | null;
  fulfilmentMode?: string | null;
  specimen?: LabOrderSpecimen | null;
  prepType?: string | null;
  prepNote?: string | null;
  questions?: LabOrderQuestion[] | null;
  members?: LabOrderMember[] | null;
}

export interface LabOrderItemDetail extends LabOrderSearchItem {
  members?: LabOrderMember[] | null;
  switchesAvailable?: boolean;
  sendsTestsToLabs?: boolean;
  physiciansMayChangeLab?: boolean;
}

export interface LabOrderSearchResult {
  search?: string | null;
  items: LabOrderSearchItem[];
  clinicTotal?: number;
  standardTotal?: number;
  standardListSearched?: boolean;
  switchesAvailable?: boolean;
  sendsTestsToLabs?: boolean;
  physiciansMayChangeLab?: boolean;
}

export type LabOrderSearchMode = 'v3' | 'legacy' | 'pending';

/** True for Master's flag-off answer (404 with one of the two codes). */
export function isLabOrderSearchOff(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 404) return false;
  return LAB_ORDER_SEARCH_OFF_CODES.some((code) => err.body.includes(code));
}

const MIN_QUERY_LENGTH = 2;

/** Used only when no QueryClientProvider is mounted (some isolated component tests): it never fetches. */
const idleClient = new QueryClient({ defaultOptions: { queries: { enabled: false, retry: false } } });

/**
 * Whether the popup runs on the order-search API here. One probe per session (5 min), shared by
 * every open of the popup; `retry: false` so a flag-off 404 answers at once. With no
 * QueryClientProvider mounted there is no probe and the answer is 'legacy'.
 */
export function useLabOrderSearchMode(enabled = true): LabOrderSearchMode {
  const client = useContext(QueryClientContext);
  const active = enabled && !!client;
  const probe = useQuery<boolean>(
    {
      queryKey: ['lab-order-search', 'available'],
      enabled: active,
      retry: false,
      staleTime: 5 * 60_000,
      queryFn: async ({ signal }) => {
        try {
          const res = await get<LabOrderSearchResult>(`${BASE}?take=1`, { signal });
          return !!res && Array.isArray(res.items) && typeof res.switchesAvailable === 'boolean';
        } catch {
          // DELIBERATE: a flag-off 404 (isLabOrderSearchOff), a 503 before Master's lab scripts run, and
          // any other failure all keep the current popup. Nothing is half-done at this point: the
          // physician has not searched yet.
          return false;
        }
      },
    },
    client ?? idleClient,
  );
  if (!active) return 'legacy';
  if (probe.data === undefined) return 'pending';
  return probe.data ? 'v3' : 'legacy';
}

/** The popup's search. Two characters at least; the dialog debounces nothing (the field is short). */
export function useLabOrderSearch(query: string, enabled = true) {
  const q = query.trim();
  return useQuery<LabOrderSearchResult>({
    queryKey: ['lab-order-search', 'search', q],
    enabled: enabled && q.length >= MIN_QUERY_LENGTH,
    retry: false,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ search: q, take: '20' });
      const res = await get<LabOrderSearchResult>(`${BASE}?${params.toString()}`, { signal });
      return { ...res, items: res?.items ?? [] };
    },
  });
}

/** One picked item with its members, ticks and labs. */
export function useLabOrderItem(id: string | null | undefined) {
  const itemId = id?.trim() ?? '';
  return useQuery<LabOrderItemDetail>({
    queryKey: ['lab-order-search', 'item', itemId],
    enabled: itemId.length > 0,
    retry: false,
    staleTime: 60_000,
    queryFn: ({ signal }) => get<LabOrderItemDetail>(`${BASE}/${encodeURIComponent(itemId)}`, { signal }),
  });
}

// ── The A&P rail and Add Order drawer (PROD-2931) ──────────────────────────────────────────────

/**
 * A lab picked from the A&P rail / Add Order drawer while the order search is on: the
 * CodeSearchResult every order path already carries (`id`, `code`, `display`), plus the order-search
 * row itself. The popup opens that exact item by its id (no second search by name), and the row
 * survives the drag payload because it is plain JSON.
 */
export interface LabOrderSearchPick extends CodeSearchResult {
  orderSearchItem: LabOrderSearchItem;
}

/** The rail row's raw `item` for one order-search result. */
export function toLabOrderSearchPick(item: LabOrderSearchItem): LabOrderSearchPick {
  const code = item.clinicCode?.trim() || item.loinc?.trim() || item.code?.trim() || undefined;
  return {
    id: item.id,
    display: item.name,
    ...(code ? { code } : {}),
    orderSearchItem: item,
  };
}

/** The order-search row a seeded lab carries, or null for any other seed (a favourite, a legacy row). */
export function orderSearchItemOf(seed: unknown): LabOrderSearchItem | null {
  if (!seed || typeof seed !== 'object') return null;
  const item = (seed as { orderSearchItem?: unknown }).orderSearchItem;
  if (!item || typeof item !== 'object') return null;
  const { id, name } = item as { id?: unknown; name?: unknown };
  return typeof id === 'string' && id.trim() && typeof name === 'string' ? (item as LabOrderSearchItem) : null;
}
